//@flow
import type {
  DirSvcMapping,
  DirSvcMappingEntry,
  DirSvcNode,
  Dispatch,
  GetState,
} from 'state/types';
import {
  getActiveDirectoryServiceMapping,
  getDefaultRole,
  getDirectoryServiceDescription,
  getDirectoryServiceDestination,
  getDirectoryServiceQueryType,
  getIdentitySession,
  getSelectedDirectoryServiceNode,
} from 'state/selectors';
import { actions as dirSvcActions } from 'state/reducers/dirSvcReducer';
import {
  addNotification,
  SUCCESS_LEVEL,
} from 'portfolio-shared/state/commands/globalNotificationsCommands';
import {
  AD_MAPPING_QUERIES,
  QUERY_TYPE_NAME,
  NAME_QUERY_OP,
} from 'util/dirSvcUtils';

const {
  addMappings: addMappingsAction,
  getDirectoryServiceMappings: getDirectoryServiceMappingsAction,
  queryDirectoryService: queryDirectoryServiceAction,
  setMapping: setMappingAction,
  setQueryPending: setQueryPendingAction,
  syncMappings: syncMappingsAction,
  updateMapping: updateMappingAction,
} = dirSvcActions;

function getQuery(
  selectedNode: ?DirSvcNode,
  queryType: string,
  operator: ?string,
  searchText: ?string
): string {
  if (selectedNode) {
    return 'memberOf=' + selectedNode.dn;
  } else {
    let query = AD_MAPPING_QUERIES[queryType].query;
    searchText = searchText || '';
    searchText =
      operator === NAME_QUERY_OP.EQUALS ? searchText : searchText.trim();
    if (queryType === QUERY_TYPE_NAME) {
      switch (operator) {
        case NAME_QUERY_OP.CONTAINS:
          query = query + (searchText ? '*' + searchText + '*' : '');
          break;
        case NAME_QUERY_OP.STARTS_WITH:
          query = query + (searchText ? searchText + '*' : '');
          break;
        default:
        case NAME_QUERY_OP.EQUALS:
          query = query + searchText;
          break;
      }
    }
    return query;
  }
}

export function queryDirectoryService(operator: ?string, searchText: ?string) {
  return async function queryDirectoryService(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getIdentitySession(getState());
    const dirSvcDescription = getDirectoryServiceDescription(getState());
    const queryType = getDirectoryServiceQueryType(getState());
    const selectedNode = getSelectedDirectoryServiceNode(getState());
    if (session && dirSvcDescription) {
      const dirSvcUuid = dirSvcDescription.uuid;
      const defaultSearchBase = dirSvcDescription.defaultSearchBase;
      const query = getQuery(selectedNode, queryType, operator, searchText);
      const searchScope = '2'; // TODO: this will change when we browse inside groups
      await dispatch(setQueryPendingAction(true));
      try {
        await dispatch(
          queryDirectoryServiceAction(
            session,
            dirSvcUuid,
            defaultSearchBase,
            searchScope,
            query
          )
        );
      } finally {
        await dispatch(setQueryPendingAction(false));
      }
    }
  };
}

export function addOrUpdateMappingEntries(entries: Array<DirSvcMappingEntry>) {
  return async function addOrUpdateMappingEntries(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const defaultRole = getDefaultRole(getState());
    const defaultRoleUuid = defaultRole ? defaultRole.uuid : '';
    const dirSvcDescription = getDirectoryServiceDescription(getState());
    const dirSvcUuid = (dirSvcDescription && dirSvcDescription.uuid) || '';
    const destination = getDirectoryServiceDestination(getState());
    const mapping: DirSvcMapping = getActiveDirectoryServiceMapping(
      getState()
    ) || {
      identityServiceUuid: dirSvcUuid,
      targetGroupGuid: destination,
      uuid: '',
      entries: [],
    };

    // Process the existing entries and update roles
    const newEntries = mapping.entries.reduce((acc, existing) => {
      const newEntry = entries.find(old => old.sourceDN === existing.sourceDN);
      if (newEntry && newEntry.roleOverrideGuid) {
        acc.push(newEntry);
      } else {
        acc.push(existing);
      }
      return acc;
    }, []);
    // Process the new entries and add them if they don't already exist.
    entries.forEach(newEntry => {
      const existing = newEntries.find(e => e.sourceDN === newEntry.sourceDN);
      if (!existing) {
        if (!newEntry.roleOverrideGuid) {
          newEntry.roleOverrideGuid = defaultRoleUuid;
        }
        newEntries.push(newEntry); // TODO - order?
      }
    });
    // add or update the mapping
    dispatch(setMappingAction({ ...mapping, entries: newEntries }));
  };
}

export function removeMappingEntry(entry: DirSvcMappingEntry) {
  return async function removeMappingEntry(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const mapping: ?DirSvcMapping = getActiveDirectoryServiceMapping(
      getState()
    );
    if (mapping) {
      // Remove the existing mapping
      const newEntries = mapping.entries.filter(
        oldEntry => oldEntry.sourceDN !== entry.sourceDN
      );
      dispatch(setMappingAction({ ...mapping, entries: newEntries }));
    }
  };
}

export function getMappings() {
  return async function getMappings(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    const dirSvcDescription = getDirectoryServiceDescription(getState());
    if (session && dirSvcDescription) {
      const dirSvcUuid = dirSvcDescription.uuid;
      await dispatch(getDirectoryServiceMappingsAction(session, dirSvcUuid));
    }
  };
}

export function saveMapping(successMessage: string, hasDeletes: boolean) {
  return async function saveMapping(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    const dirSvcDescription = getDirectoryServiceDescription(getState());
    const dirSvcDescriptionUuid =
      (dirSvcDescription && dirSvcDescription.uuid) || '';
    const mapping = getActiveDirectoryServiceMapping(getState());
    const isDirty =
      hasDeletes || (mapping && mapping.entries.find(e => e.unsaved));

    if (
      session &&
      dirSvcDescription &&
      dirSvcDescription.uuid &&
      mapping &&
      isDirty
    ) {
      if (mapping.uuid) {
        await dispatch(
          updateMappingAction(session, dirSvcDescription.uuid, mapping)
        );
      } else {
        mapping.identityServiceUuid = dirSvcDescriptionUuid;
        await dispatch(
          addMappingsAction(session, dirSvcDescription.uuid, [mapping])
        );
      }
      await dispatch(syncMappingsAction(session, dirSvcDescription.uuid));
      dispatch(addNotification(successMessage, SUCCESS_LEVEL));
      await dispatch(
        getDirectoryServiceMappingsAction(session, dirSvcDescription.uuid)
      );
    }
  };
}

export function syncMappings(successMessage: string) {
  return async function syncMappings(dispatch: Dispatch, getState: GetState) {
    const session = getIdentitySession(getState());
    const dirSvcDescription = getDirectoryServiceDescription(getState());
    if (session && dirSvcDescription) {
      await dispatch(syncMappingsAction(session, dirSvcDescription.uuid));
      dispatch(addNotification(successMessage, SUCCESS_LEVEL));
    }
  };
}



// WEBPACK FOOTER //
// ./src/state/commands/dirSvcCommands.js